import React, { useEffect, useRef, useState, useCallback } from 'react';

// ===== Endpoints (all via backend proxy to avoid mixed-content in prod HTTPS) =====
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const TRANSLATE_ENDPOINT = `${API_BASE}/swahili/translate`;
const TTS_SWAHILI_ENDPOINT = `${API_BASE}/swahili/tts/swahili`;
const TTS_FRENCH_ENDPOINT = `${API_BASE}/swahili/tts/french`;

// Build WebSocket URL from API_BASE (http→ws, https→wss)
function buildWsUrl(lang) {
  try {
    const u = new URL(API_BASE, window.location.origin);
    const wsProto = u.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProto}//${u.host}${u.pathname.replace(/\/+$/, '')}/swahili/ws/asr?lang=${encodeURIComponent(lang)}`;
  } catch {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/api/swahili/ws/asr?lang=${encodeURIComponent(lang)}`;
  }
}

const ASR_CONFIG = {
  swh_Latn: {
    http: `${API_BASE}/swahili/asr?lang=swh_Latn`,
    filename: 'swahili_recording.webm',
  },
  fra_Latn: {
    http: `${API_BASE}/swahili/asr?lang=fra_Latn`,
    filename: 'french_recording.webm',
  },
};

const LANG_LABELS = { fra_Latn: 'French', swh_Latn: 'Swahili' };

const EXAMPLES = {
  fra_Latn: [
    "Bonjour, je veux savoir comment acheter du crédit internet pour mon téléphone.",
    "Monsieur le Président, c'est un honneur de vous rencontrer au sommet Africa Forward.",
    "Quelles sont les priorités communes entre la France et le Kenya pour les cinq prochaines années ?",
    "Nous souhaitons renforcer la coopération économique et technologique entre nos deux nations.",
    "Comment puis-je renouveler mon forfait internet depuis l'application ?",
    "Le partenariat avec Orange nous permet de connecter davantage de villages ruraux.",
  ],
  swh_Latn: [
    "Habari, ninataka kujua jinsi ya kununua muda wa intaneti kwa simu yangu.",
    "Mheshimiwa Rais, ni heshima kubwa kukutana nawe katika mkutano wa Africa Forward.",
    "Vipaumbele vyetu vya pamoja kwa miaka mitano ijayo ni vipi?",
    "Tunatarajia kuimarisha ushirikiano wa kiuchumi na kiteknolojia kati ya nchi zetu.",
    "Naweza kusasisha kifurushi changu cha intaneti kupitia programu?",
    "Ushirikiano na Orange unatusaidia kuunganisha vijiji vingi zaidi.",
  ],
};

// ===== Helpers =====
function extractTranslationFromResponse(data) {
  if (typeof data === 'string') return data.trim() || null;
  if (!data || typeof data !== 'object') return null;
  const keys = ['translation', 'translated_text', 'translatedText', 'result', 'output', 'text', 'response'];
  for (const key of keys) {
    const v = data[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  for (const nested of ['result', 'data']) {
    if (data[nested] && typeof data[nested] === 'object') {
      for (const key of ['translation', 'translated_text', 'translatedText', 'text', 'output']) {
        const v = data[nested][key];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
    }
  }
  return null;
}

function downsampleBuffer(buffer, inputSampleRate, outputSampleRate) {
  if (outputSampleRate === inputSampleRate) return buffer;
  const ratio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    const nextOffset = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffset && i < buffer.length; i++) {
      accum += buffer[i];
      count++;
    }
    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult++;
    offsetBuffer = nextOffset;
  }
  return result;
}

function convertFloat32ToInt16(buffer) {
  const l = buffer.length;
  const result = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    const s = Math.max(-1, Math.min(1, buffer[i]));
    result[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return result;
}

const SwahiliFrenchTranslator = () => {
  const [sourceLang, setSourceLang] = useState('fra_Latn');
  const [targetLang, setTargetLang] = useState('swh_Latn');
  const [sourceText, setSourceText] = useState(EXAMPLES.fra_Latn[0]);
  const [translatedText, setTranslatedText] = useState('');
  const [selectedExample, setSelectedExample] = useState(EXAMPLES.fra_Latn[0]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [status, setStatus] = useState('');

  const [speaker, setSpeaker] = useState('french');
  const [audioUrl, setAudioUrl] = useState(null);
  const [ttsBusy, setTtsBusy] = useState(false);

  const [isRecordingFull, setIsRecordingFull] = useState(false);
  const [isLiveRecording, setIsLiveRecording] = useState(false);

  const translateTimeout = useRef(null);
  const lastAudioUrl = useRef(null);
  const audioRef = useRef(null);

  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordedChunksRef = useRef([]);

  const liveStreamRef = useRef(null);
  const liveSocketRef = useRef(null);
  const liveAudioContextRef = useRef(null);
  const liveProcessorRef = useRef(null);
  const liveSourceRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const partialTranscriptRef = useRef('');

  const getCurrentASRConfig = useCallback(
    () => ASR_CONFIG[sourceLang] || ASR_CONFIG.fra_Latn,
    [sourceLang]
  );

  const translate = useCallback(async (text, src, tgt) => {
    const t = (text || '').trim();
    if (!t) {
      setTranslatedText('');
      setStatus('');
      return;
    }
    setIsTranslating(true);
    try {
      const response = await fetch(TRANSLATE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: t, src_lang: src, tgt_lang: tgt }),
      });
      let data;
      try {
        data = await response.json();
      } catch {
        data = await response.text();
      }
      if (!response.ok) {
        console.error('Erreur traduction:', data);
        setTranslatedText('');
        setStatus('Translation error.');
        return;
      }
      const extracted = extractTranslationFromResponse(data);
      setTranslatedText(extracted || '');
      setStatus('');
    } catch (err) {
      console.error('Erreur réseau traduction:', err);
      setTranslatedText('');
      setStatus('Connection error to translation API.');
    } finally {
      setIsTranslating(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(translateTimeout.current);
    translateTimeout.current = setTimeout(() => {
      translate(sourceText, sourceLang, targetLang);
    }, 500);
    return () => clearTimeout(translateTimeout.current);
  }, [sourceText, sourceLang, targetLang, translate]);

  useEffect(() => {
    return () => {
      stopFullRecordingInternal();
      stopLiveRecordingInternal();
      if (lastAudioUrl.current) URL.revokeObjectURL(lastAudioUrl.current);
    };
  }, []);

  const playTTS = async () => {
    const text = translatedText.trim();
    if (!text) {
      setStatus('Nothing to read.');
      return;
    }
    setTtsBusy(true);
    setStatus('Generating audio...');
    try {
      let response;
      if (targetLang === 'swh_Latn') {
        response = await fetch(TTS_SWAHILI_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, normalize: true, return_json_metadata: false }),
        });
      } else {
        const body = JSON.stringify({ text, language: 'fr', speaker_name: speaker });
        response = await fetch(TTS_FRENCH_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
      }
      if (!response.ok) {
        console.error('Erreur TTS');
        setStatus('TTS error.');
        return;
      }
      const blob = await response.blob();
      if (lastAudioUrl.current) URL.revokeObjectURL(lastAudioUrl.current);
      const url = URL.createObjectURL(blob);
      lastAudioUrl.current = url;
      setAudioUrl(url);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().then(
            () => setStatus(''),
            () => setStatus('Audio ready.')
          );
        }
      }, 60);
    } catch (err) {
      console.error('Erreur réseau TTS:', err);
      setStatus('Connection error to TTS API.');
    } finally {
      setTtsBusy(false);
    }
  };

  const resetAudio = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {}
    }
    if (lastAudioUrl.current) {
      URL.revokeObjectURL(lastAudioUrl.current);
      lastAudioUrl.current = null;
    }
    setAudioUrl(null);
  };

  const handleSwap = () => {
    stopFullRecordingIfNeeded();
    stopLiveRecordingIfNeeded();
    resetAudio();
    const oldSrc = sourceLang;
    const oldTgt = targetLang;
    const oldSrcText = sourceText;
    const oldTrans = translatedText;
    setSourceLang(oldTgt);
    setTargetLang(oldSrc);
    setSourceText(oldTrans);
    setTranslatedText(oldSrcText);
    setSelectedExample('');
    finalTranscriptRef.current = '';
    partialTranscriptRef.current = '';
    setStatus('');
  };

  const handleSourceLangChange = (e) => {
    stopFullRecordingIfNeeded();
    stopLiveRecordingIfNeeded();
    resetAudio();
    const v = e.target.value;
    setSourceLang(v);
    setTargetLang(v === 'fra_Latn' ? 'swh_Latn' : 'fra_Latn');
    const first = EXAMPLES[v]?.[0] || '';
    setSourceText(first);
    setSelectedExample(first);
  };

  const handleTargetLangChange = (e) => {
    resetAudio();
    let v = e.target.value;
    if (v === sourceLang) v = sourceLang === 'fra_Latn' ? 'swh_Latn' : 'fra_Latn';
    setTargetLang(v);
  };

  const handleExampleClick = (ex) => {
    setSourceText(ex);
    setSelectedExample(ex);
  };

  useEffect(() => {
    if (sourceText !== selectedExample && selectedExample) setSelectedExample('');
  }, [sourceText, selectedExample]);

  // Full recording
  const startFullRecording = async () => {
    try {
      stopLiveRecordingIfNeeded();
      recordedChunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        try {
          const blob = new Blob(recordedChunksRef.current, { type: mimeType });
          await sendFullAudioToASR(blob);
        } catch (err) {
          console.error('Erreur envoi ASR:', err);
          setStatus('Error sending audio to ASR.');
        } finally {
          cleanupFullRecording();
        }
      };

      recorder.start();
      setIsRecordingFull(true);
      setStatus('Recording...');
    } catch (err) {
      console.error('Erreur micro:', err);
      setStatus('Cannot access microphone.');
    }
  };

  const stopFullRecordingInternal = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {}
    }
  };

  const stopFullRecording = () => {
    stopFullRecordingInternal();
    setIsRecordingFull(false);
    setStatus('Sending audio to ASR...');
  };

  const stopFullRecordingIfNeeded = () => {
    if (isRecordingFull || mediaRecorderRef.current) stopFullRecording();
  };

  const cleanupFullRecording = () => {
    const stream = recordingStreamRef.current;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    recordingStreamRef.current = null;
    mediaRecorderRef.current = null;
    setIsRecordingFull(false);
  };

  const toggleFullRecording = () => {
    if (isRecordingFull) stopFullRecording();
    else startFullRecording();
  };

  const sendFullAudioToASR = async (blob) => {
    const asrConfig = getCurrentASRConfig();
    const formData = new FormData();
    formData.append('file', blob, asrConfig.filename);
    const response = await fetch(asrConfig.http, { method: 'POST', body: formData });
    let data;
    try {
      data = await response.json();
    } catch {
      data = await response.text();
    }
    if (!response.ok) {
      console.error('Erreur ASR full:', data);
      setStatus('ASR error.');
      return;
    }
    const text = typeof data?.text === 'string' ? data.text.trim() : '';
    setSourceText(text);
    setSelectedExample('');
    finalTranscriptRef.current = text;
    partialTranscriptRef.current = '';
    setStatus(text ? '' : 'No transcription detected.');
  };

  // Live recording
  const startLiveRecording = async () => {
    try {
      stopFullRecordingIfNeeded();
      finalTranscriptRef.current = '';
      partialTranscriptRef.current = '';
      setSourceText('');
      setSelectedExample('');

      // For French, use the browser's Web Speech API (no server, no WS)
      if (sourceLang === 'fra_Latn') {
        const SpeechRecognitionImpl =
          window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognitionImpl) {
          setStatus(
            'Live transcription not supported in this browser. Use Chrome/Edge.'
          );
          return;
        }
        const recognition = new SpeechRecognitionImpl();
        recognition.lang = 'fr-FR';
        recognition.continuous = true;
        recognition.interimResults = true;

        recognition.onresult = (event) => {
          let interim = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const res = event.results[i];
            const transcript = res[0].transcript;
            if (res.isFinal) {
              finalTranscriptRef.current = [
                finalTranscriptRef.current,
                transcript.trim(),
              ]
                .filter(Boolean)
                .join(' ')
                .trim();
            } else {
              interim += transcript;
            }
          }
          partialTranscriptRef.current = interim.trim();
          const combined = [
            finalTranscriptRef.current,
            partialTranscriptRef.current,
          ]
            .filter(Boolean)
            .join(' ')
            .trim();
          setSourceText(combined);
        };

        recognition.onerror = (e) => {
          console.error('SpeechRecognition error:', e);
          if (e.error !== 'no-speech' && e.error !== 'aborted') {
            setStatus('Live ASR error: ' + e.error);
          }
        };

        recognition.onend = () => {
          speechRecognitionRef.current = null;
          setIsLiveRecording(false);
        };

        speechRecognitionRef.current = recognition;
        recognition.start();
        setIsLiveRecording(true);
        setStatus('Live transcription started...');
        return;
      }

      // Swahili (and others): use the remote WebSocket ASR
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      liveStreamRef.current = stream;

      const socket = new WebSocket(buildWsUrl(sourceLang));
      socket.binaryType = 'arraybuffer';
      liveSocketRef.current = socket;

      socket.onopen = async () => {
        setStatus('Live transcription started...');
        await setupLiveAudioPipeline();
        setIsLiveRecording(true);
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.error) {
            setStatus('Live ASR error.');
            return;
          }
          if (typeof data.text === 'string') {
            const cleanText = data.text.trim();
            if (data.final) {
              finalTranscriptRef.current = [finalTranscriptRef.current, cleanText]
                .filter(Boolean)
                .join(' ')
                .trim();
              partialTranscriptRef.current = '';
            } else {
              partialTranscriptRef.current = cleanText;
            }
            const combined = [finalTranscriptRef.current, partialTranscriptRef.current]
              .filter(Boolean)
              .join(' ')
              .trim();
            setSourceText(combined);
          }
        } catch (err) {
          console.error('Message WS invalide:', err);
        }
      };

      socket.onerror = () => setStatus('Live ASR connection error.');
      socket.onclose = () => {
        teardownLiveAudioPipeline();
        setIsLiveRecording(false);
      };
    } catch (err) {
      console.error('Erreur micro/live:', err);
      setStatus('Cannot start live transcription.');
      stopLiveRecording();
    }
  };

  const setupLiveAudioPipeline = async () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    liveAudioContextRef.current = ctx;
    const source = ctx.createMediaStreamSource(liveStreamRef.current);
    liveSourceRef.current = source;
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    liveProcessorRef.current = processor;
    processor.onaudioprocess = (event) => {
      const socket = liveSocketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      const input = event.inputBuffer.getChannelData(0);
      const downsampled = downsampleBuffer(input, ctx.sampleRate, 16000);
      const pcm16 = convertFloat32ToInt16(downsampled);
      if (pcm16.byteLength > 0) socket.send(pcm16.buffer);
    };
    source.connect(processor);
    processor.connect(ctx.destination);
  };

  const stopLiveRecordingInternal = () => {
    // If browser SpeechRecognition is active, stop it
    const recognition = speechRecognitionRef.current;
    if (recognition) {
      try {
        recognition.stop();
      } catch {}
      speechRecognitionRef.current = null;
      setIsLiveRecording(false);
      return;
    }
    const socket = liveSocketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.close();
      } catch {}
    } else {
      teardownLiveAudioPipeline();
      setIsLiveRecording(false);
    }
  };

  const stopLiveRecording = () => stopLiveRecordingInternal();

  const stopLiveRecordingIfNeeded = () => {
    if (
      isLiveRecording ||
      liveSocketRef.current ||
      speechRecognitionRef.current
    )
      stopLiveRecording();
  };

  const teardownLiveAudioPipeline = () => {
    try {
      if (liveProcessorRef.current) {
        liveProcessorRef.current.disconnect();
        liveProcessorRef.current.onaudioprocess = null;
      }
    } catch {}
    try {
      liveSourceRef.current?.disconnect();
    } catch {}
    try {
      liveAudioContextRef.current?.close();
    } catch {}
    liveStreamRef.current?.getTracks().forEach((t) => t.stop());
    liveProcessorRef.current = null;
    liveSourceRef.current = null;
    liveAudioContextRef.current = null;
    liveStreamRef.current = null;
    liveSocketRef.current = null;
  };

  const toggleLiveRecording = () => {
    if (isLiveRecording) stopLiveRecording();
    else startLiveRecording();
  };

  return (
    <div
      style={{
        maxWidth: '1100px',
        margin: '0 auto',
        padding: '24px 20px',
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h1
          style={{
            fontSize: 30,
            fontWeight: 700,
            color: '#1f2937',
            margin: 0,
            letterSpacing: -0.3,
          }}
        >
          Swahili ↔ French Live Translator
        </h1>
        <p style={{ fontSize: 15, color: '#6b7280', marginTop: 8 }}>
          Real-time speech &amp; text translation — “Africa Forward” summit demo.
        </p>
      </div>

      {/* Main card */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: 16,
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
          border: '1px solid #eef0f4',
          overflow: 'hidden',
        }}
      >
        {/* Language bar */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 56px 1fr',
            alignItems: 'center',
            padding: '14px 16px',
            borderBottom: '1px solid #eef0f4',
            background: '#fafbfc',
          }}
        >
          <LangPills
            value={sourceLang}
            onChange={(v) =>
              handleSourceLangChange({ target: { value: v } })
            }
          />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={handleSwap}
              title="Swap languages"
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '1px solid #e5e7eb',
                background: '#ffffff',
                color: '#4b5563',
                fontSize: 18,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all .15s ease',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseOut={(e) => (e.currentTarget.style.background = '#ffffff')}
            >
              ⇄
            </button>
          </div>
          <LangPills
            value={targetLang}
            onChange={(v) =>
              handleTargetLangChange({ target: { value: v } })
            }
          />
        </div>

        {/* Text panes */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1px 1fr',
            minHeight: 320,
          }}
        >
          {/* Source pane */}
          <div style={{ position: 'relative', padding: '18px 20px 64px' }}>
            <textarea
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              placeholder="Enter text or use the microphone..."
              style={paneTextareaStyle}
            />
            <div style={paneToolbarStyle}>
              <button
                type="button"
                onClick={toggleFullRecording}
                title={isRecordingFull ? 'Stop recording' : 'Record & transcribe'}
                style={{
                  ...toolBtnStyle,
                  ...(isRecordingFull ? toolBtnActiveRed : {}),
                }}
              >
                {isRecordingFull ? '⏹' : '🎙'}
              </button>
              <button
                type="button"
                onClick={toggleLiveRecording}
                title={
                  isLiveRecording
                    ? 'Stop live transcription'
                    : 'Live transcription'
                }
                style={{
                  ...toolBtnStyle,
                  ...(isLiveRecording ? toolBtnActiveGreen : {}),
                }}
              >
                {isLiveRecording ? '⏹' : '🟢'}
              </button>
              <span style={charCountStyle}>{sourceText.length}</span>
            </div>
          </div>

          <div style={{ background: '#eef0f4' }} />

          {/* Target pane */}
          <div
            style={{
              position: 'relative',
              padding: '18px 20px 64px',
              background: '#fcfcfd',
            }}
          >
            <textarea
              readOnly
              value={translatedText}
              placeholder="Translation"
              style={{ ...paneTextareaStyle, background: 'transparent' }}
            />

            {isTranslating && (
              <div style={translatingBadgeStyle}>Translating…</div>
            )}

            <div style={paneToolbarStyle}>
              <button
                onClick={playTTS}
                disabled={ttsBusy || !translatedText}
                title="Listen to translation"
                style={{
                  ...toolBtnStyle,
                  opacity: ttsBusy || !translatedText ? 0.5 : 1,
                  cursor:
                    ttsBusy || !translatedText ? 'not-allowed' : 'pointer',
                }}
              >
                {ttsBusy ? '⏳' : '🔊'}
              </button>

              {targetLang === 'fra_Latn' && (
                <select
                  value={speaker}
                  onChange={(e) => {
                    resetAudio();
                    setSpeaker(e.target.value);
                  }}
                  title="Speaker voice"
                  style={speakerSelectStyle}
                >
                  <option value="french">Féminin voice 1</option>
                  <option value="jacline">Féminin voice 2</option>
                  <option value="remy">Masculin voice 1</option>
                  <option value="macron">Masculin voice 2</option>
                </select>
              )}

              {audioUrl && (
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  controls
                  style={{ height: 36, marginLeft: 'auto', maxWidth: 240 }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status */}
      {status && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            backgroundColor: '#eef2ff',
            color: '#3730a3',
            borderRadius: 10,
            fontSize: 14,
          }}
        >
          {status}
        </div>
      )}

      {/* Examples */}
      <div
        style={{
          marginTop: 24,
          backgroundColor: '#ffffff',
          padding: '20px 22px',
          borderRadius: 14,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          border: '1px solid #eef0f4',
        }}
      >
        <h3
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#374151',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            margin: 0,
            marginBottom: 14,
          }}
        >
          Example phrases · {LANG_LABELS[sourceLang]}
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 10,
          }}
        >
          {(EXAMPLES[sourceLang] || []).map((ex, i) => (
            <button
              key={i}
              onClick={() => handleExampleClick(ex)}
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                border:
                  ex === selectedExample
                    ? '1px solid #6366f1'
                    : '1px solid #e5e7eb',
                backgroundColor:
                  ex === selectedExample ? '#eef2ff' : '#f9fafb',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all .15s ease',
                fontSize: 14,
                color: '#1f2937',
                lineHeight: 1.4,
              }}
              onMouseOver={(e) => {
                if (ex !== selectedExample)
                  e.currentTarget.style.background = '#f3f4f6';
              }}
              onMouseOut={(e) => {
                if (ex !== selectedExample)
                  e.currentTarget.style.background = '#f9fafb';
              }}
            >
              {ex}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===== Sub-components =====
const LangPills = ({ value, onChange }) => {
  const langs = [
    { code: 'fra_Latn', label: 'French' },
    { code: 'swh_Latn', label: 'Swahili' },
  ];
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
      {langs.map((l) => {
        const active = l.code === value;
        return (
          <button
            key={l.code}
            onClick={() => onChange(l.code)}
            style={{
              padding: '8px 18px',
              borderRadius: 999,
              border: active ? '1px solid #6366f1' : '1px solid #e5e7eb',
              background: active ? '#eef2ff' : '#ffffff',
              color: active ? '#3730a3' : '#374151',
              fontSize: 14,
              fontWeight: active ? 600 : 500,
              cursor: 'pointer',
              transition: 'all .15s ease',
            }}
          >
            {l.label}
          </button>
        );
      })}
    </div>
  );
};

// ===== Styles =====
const paneTextareaStyle = {
  width: '100%',
  minHeight: 240,
  border: 'none',
  outline: 'none',
  resize: 'none',
  fontSize: 18,
  lineHeight: 1.5,
  color: '#111827',
  background: 'transparent',
  fontFamily:
    'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
};

const paneToolbarStyle = {
  position: 'absolute',
  left: 16,
  right: 16,
  bottom: 14,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const toolBtnStyle = {
  width: 38,
  height: 38,
  borderRadius: '50%',
  border: '1px solid #e5e7eb',
  background: '#ffffff',
  cursor: 'pointer',
  fontSize: 16,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#4b5563',
  transition: 'all .15s ease',
};

const toolBtnActiveRed = {
  background: '#fee2e2',
  borderColor: '#fca5a5',
  color: '#b91c1c',
};

const toolBtnActiveGreen = {
  background: '#dcfce7',
  borderColor: '#86efac',
  color: '#166534',
};

const speakerSelectStyle = {
  height: 36,
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  background: '#ffffff',
  fontSize: 13,
  padding: '0 10px',
  color: '#374151',
  cursor: 'pointer',
};

const charCountStyle = {
  marginLeft: 'auto',
  fontSize: 12,
  color: '#9ca3af',
};

const translatingBadgeStyle = {
  position: 'absolute',
  top: 12,
  right: 16,
  backgroundColor: '#ffffff',
  padding: '3px 10px',
  borderRadius: 999,
  fontSize: 12,
  color: '#6b7280',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

export default SwahiliFrenchTranslator;
